/**
 * Teletype dispatch feed (bottom-left): engine events printed as terse staff dispatches on a
 * paper roll. New lines type in character by character (red ribbon for urgent ones); clicking a
 * line focuses the camera on its hex.
 *
 * Each event is worded once, on arrival, in every language (names resolve against that tick's
 * view), so switching the language re-prints the whole roll without re-wording history. The
 * German roll is set like a period Fernschreiben: capitals, Ä/Ö/Ü as AE/OE/UE, ß as SS.
 */
import { dayOf, type GameEvent } from '@friction/engine';
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { prefersReducedMotion } from '../../app/settings';
import { bus } from '../../game/bus';
import { type DispatchCategory, type DispatchLine, describeEvent, formatClock } from '../../game/format';
import { useGame } from '../../game/store';
import { inEveryLocale, type Locale, type Localized, useT } from '../../i18n';
import { cx } from '../common/controls';
import { Icon } from '../common/Icon';
import hud from './hud.module.css';
import { hudStore, useHud } from './hudStore';
import s from './teletype.module.css';

/** Characters per second of the printer. */
const CPS = 70;
/** More queued lines than this and the printer catches up instantly. */
const MAX_BACKLOG = 4;
/** Lines kept on the roll. */
const MAX_LINES = 90;

const FILTERS: (DispatchCategory | 'all')[] = ['all', 'front', 'orders', 'intel', 'home'];

const TELEPRINTER: Record<string, string> = { Ä: 'AE', Ö: 'OE', Ü: 'UE', ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };

/** The printer's character set: a German teleprinter had no umlauts (and prints in capitals). */
const teleprint = (text: string, locale: Locale): string =>
  locale === 'de' ? text.replace(/[ÄÖÜäöüß]/g, (c) => TELEPRINTER[c]) : text;

const fullText = (d: DispatchLine, locale: Locale) =>
  teleprint(d.head ? `${d.head} — ${d.text}` : d.text, locale);

export function Teletype() {
  const t = useT();
  const log = useGame((g) => g.log);
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  const filter = useHud((h) => h.feedFilter);
  const expanded = useHud((h) => h.feedExpanded);

  // Each event is worded once, when it first arrives (names resolve against that tick's view).
  const cache = useRef(new WeakMap<GameEvent, Localized<DispatchLine>>());
  const all = useMemo(() => {
    if (!view || !map || !grid) return [];
    const ctx = { view, map, grid, log };
    return log.slice(-MAX_LINES * 2).map((ev) => {
      let worded = cache.current.get(ev);
      if (!worded) {
        worded = inEveryLocale((l) => describeEvent(ev, ctx, l));
        cache.current.set(ev, worded);
      }
      return { ev, d: worded[t.locale] };
    });
  }, [log, view, map, grid, t.locale]);
  const lines = useMemo(
    () => (filter === 'all' ? all : all.filter((l) => l.d.category === filter)).slice(-MAX_LINES),
    [all, filter],
  );

  // ---- printer: what was on the roll when the screen opened prints instantly; new events type in
  const printed = useRef<WeakSet<GameEvent> | null>(null);
  if (!printed.current) printed.current = new WeakSet(log);
  const done = printed.current;
  const [typing, setTyping] = useState<{ ev: GameEvent; chars: number } | null>(null);

  useEffect(() => {
    if (typing) return;
    const queue: typeof all = [];
    for (const l of all) {
      if (done.has(l.ev)) continue;
      // Traffic hidden by the filter is filed without printing.
      if (filter !== 'all' && l.d.category !== filter) done.add(l.ev);
      else queue.push(l);
    }
    if (!queue.length) return;
    if (prefersReducedMotion()) {
      for (const l of queue) done.add(l.ev);
      setTyping(null);
      return;
    }
    if (queue.length > MAX_BACKLOG) for (const l of queue.slice(0, -2)) done.add(l.ev);
    const next = queue.length > MAX_BACKLOG ? queue[queue.length - 2] : queue[0];
    if (next.d.severity === 'urgent') bus.emit('sound', { name: 'bell' });
    setTyping({ ev: next.ev, chars: 0 });
  }, [all, filter, typing, done]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: restart only when the typed line changes, not per character
  useEffect(() => {
    if (!typing) return;
    const line = all.find((l) => l.ev === typing.ev);
    if (!line) {
      done.add(typing.ev);
      setTyping(null);
      return;
    }
    const len = fullText(line.d, t.locale).length;
    let raf = 0;
    let last = performance.now();
    let chars = typing.chars;
    let lastSound = chars;
    const step = (now: number) => {
      chars += ((now - last) / 1000) * CPS;
      last = now;
      if (chars - lastSound >= 2.5) {
        lastSound = chars;
        bus.emit('sound', { name: 'type', volume: 0.5 });
      }
      if (chars >= len) {
        done.add(typing.ev);
        setTyping(null);
        return;
      }
      setTyping({ ev: typing.ev, chars });
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [typing?.ev]);

  // ---- auto-scroll unless the reader scrolled up
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  });

  const rows: ReactNode[] = [];
  let lastDay = -1;
  for (const { ev, d } of lines) {
    const isTyping = typing?.ev === ev;
    if (!isTyping && !done.has(ev)) continue;
    const day = dayOf(d.tick);
    if (lastDay !== -1 && day !== lastDay)
      rows.push(
        <div key={`day-${day}-${rows.length}`} className={s.dayRule}>
          {t('tty.dayRule', { n: day })}
        </div>,
      );
    lastDay = day;
    const text = fullText(d, t.locale);
    const shown = isTyping ? Math.floor(typing.chars) : text.length;
    const headLen = d.head ? teleprint(d.head, t.locale).length : 0;
    rows.push(
      <div
        key={rows.length}
        className={cx(
          s.line,
          d.hex >= 0 && s.clickable,
          d.severity === 'urgent' && s.urgent,
          d.severity === 'notable' && s.notable,
        )}
        onClick={d.hex >= 0 ? () => bus.emit('focusHex', { hex: d.hex }) : undefined}
        title={d.hex >= 0 ? t('ui.showOnMap') : undefined}
      >
        <span className={s.time}>{formatClock(d.tick)}</span>
        <span>
          {headLen > 0 && <span className={s.headWord}>{text.slice(0, Math.min(shown, headLen))}</span>}
          {text.slice(headLen, Math.max(headLen, shown))}
          {isTyping && <span className={s.caret} />}
        </span>
      </div>,
    );
  }

  const hs = hudStore.getState();
  return (
    <section
      className={cx(hud.plate, s.tty, expanded && s.expanded)}
      aria-label={t('tty.aria')}
      data-tut="teletype"
    >
      <div className={s.head}>
        <span className={s.title}>
          <span className={cx(s.led, typing && s.ledOn)} />
          {t('tty.title')}
        </span>
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            className={cx(s.filter, filter === f && s.filterOn)}
            onClick={() => hs.setFeedFilter(f)}
          >
            {t(`tty.filter.${f}`)}
          </button>
        ))}
        <button
          type="button"
          className={s.iconBtn}
          title={expanded ? t('tty.collapse') : t('tty.expand')}
          onClick={() => hs.toggleFeed()}
        >
          <Icon name="chevronDown" size={14} style={{ transform: expanded ? undefined : 'rotate(180deg)' }} />
        </button>
      </div>
      <div className={s.roll}>
        <div className={s.fade} />
        <div
          ref={scroller}
          className={s.lines}
          onScroll={(e) => {
            const el = e.currentTarget;
            stick.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
          }}
        >
          <div className={s.spacer} />
          {rows.length ? rows : <div className={s.empty}>{t('tty.empty')}</div>}
        </div>
      </div>
    </section>
  );
}
